import { useTranslation } from 'react-i18next';

import { displayQuarter, europeanFormatNumber, formatInstalledCapacity } from '@/helpers';
import { Button, Icon, Typography } from '@/components';
import { useDevelopmentStatus } from '@/hooks';
import { isItemNull } from '@/features/projects/helpers';

export type SolarBoxProps = {
  readonly normalizedProject: any;
  readonly onEdit?: () => void;
  readonly isEditable: boolean;
};

export function SolarBox({ normalizedProject, onEdit, isEditable }: SolarBoxProps) {
  const { t } = useTranslation(['opportunities']);
  const { map: mapDevelopmentStatus } = useDevelopmentStatus();
  const solarTech = normalizedProject.technologies.find((tech: any) => tech.type === 'solar');
  if (!solarTech) return null;

  const item = solarTech.details.item;
  const status = solarTech.details.developmentStatus.status;
  if (!isEditable && !status && isItemNull(item)) return null;

  const fields = [
    {
      label: t('Installed Capacity'),
      value: formatInstalledCapacity(item.installedCapacityNominal, item.installedCapacityPeak),
    },
    {
      label: t('Specific Production'),
      value: item.specificProduction ? `${europeanFormatNumber(item.specificProduction)} h` : null,
    },
    {
      label: t('Development Status'),
      value: mapDevelopmentStatus(status) || null,
    },
    {
      label: t('Ready to Build (RtB) Date'),
      value: item.readyToBuild ? displayQuarter(item.readyToBuild) : null,
    },
    {
      label: t('Commercial Operation Date (COD)'),
      value: item.commercialOperationDate ? displayQuarter(item.commercialOperationDate) : null,
    },
    {
      label: t('Panel Configuration'),
      value: item.panelConfiguration ?? null,
    },
    {
      label: t('Module Power'),
      value: item.modulePower ? `${europeanFormatNumber(item.modulePower, 0)} W` : null,
    },
    {
      label: t('Type of Structure'),
      value: item.structureType ?? null,
    },
    {
      label: t('Pitch Distance'),
      value: item.pitchDistance ? `${europeanFormatNumber(item.pitchDistance)} m` : null,
    },
    {
      label: t('Module Type'),
      value: item.moduleType ?? null,
    },
  ];

  const visibleFields = isEditable
    ? fields
    : fields.filter(({ value }) => value !== null && value !== undefined && value !== '');

  return (
    <div className="rounded-lg border border-neutral-50 bg-white p-6 shadow-xs">
      <div className="mb-6 flex items-center">
        <div className="flex grow items-center">
          <div className="mr-2 flex h-10 w-10 items-center justify-center rounded-full bg-neutral-50">
            <Icon name="solar" className="h-6 w-6" />
          </div>
          <Typography as="h3" weight="semibold" size="title">
            {t('Solar PV Details & Design')}
          </Typography>
        </div>
        {isEditable && (
          <Button size="sm" color="neutral" icon="edit" onClick={onEdit} className="ml-auto">
            {t('Edit')}
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1">
        {visibleFields.map(({ label, value }) => (
          <Typography key={label} as="p" size="body-2" weight="semibold" relaxed className="mb-1">
            {label}:{' '}
            <span className="ml-3 text-base font-normal text-neutral-900">{value ?? (isEditable ? '—' : null)}</span>
          </Typography>
        ))}
      </div>

      {(isEditable || item.generalComments) && (
        <Typography as="p" size="body-2" weight="semibold" relaxed>
          {t('Other Comments')}:{' '}
          <span className="ml-3 text-base font-normal whitespace-pre-wrap text-neutral-900">
            {item.generalComments ?? (isEditable ? '—' : null)}
          </span>
        </Typography>
      )}
    </div>
  );
}
